import { useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { AlertCircle, Check, Loader2 } from "lucide-react";

import { useVaultLock } from "@/components/account/medical-vault/protected-content";
import { Button } from "@/components/ui/button";
import { showToast } from "@/lib/ui/toast";
import { handleVaultLocked } from "@/lib/vault-unlock";

interface AddCardFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

// Rendered inside the wallet's ProtectedContent: the /cards routes answer 403
// vault_locked once the wallet PIN's unlock lapses. The SetupIntent read then
// brings the gate back (the form remounts closed after the unlock); the dedup
// — the card is already saved at Stripe by then — asks again and retries once.
export function AddCardForm({ onSuccess, onCancel }: AddCardFormProps) {
  const { t } = useLingui();
  const { runUnlocked } = useVaultLock();
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  useEffect(() => {
    apiFetch
      .post<{ clientSecret: string }>("/api/account/billing/cards/setup-intent")
      .then((data) => setClientSecret(data.clientSecret))
      .catch((err: unknown) => {
        if (!handleVaultLocked(err)) setError(t`Failed to initialize card form`);
      });
  }, [t]);

  const handleSubmit = async () => {
    if (!stripe || !elements || !clientSecret) return;
    setSaving(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setSaving(false);
      return;
    }

    const toast = showToast(t`Saving…`, "loading");

    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (stripeError) {
      const msg = stripeError.message || t`Card setup failed`;
      setError(msg);
      toast.update(msg, "error");
      setSaving(false);
      return;
    }

    // Server-side dedup: detach if fingerprint already exists on this customer
    const pmId =
      typeof setupIntent?.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent?.payment_method?.id;
    if (pmId) {
      try {
        const dedupData = await runUnlocked(() =>
          apiFetch.post<{ duplicate?: boolean }>("/api/account/billing/cards/dedup", {
            paymentMethodId: pmId,
          }),
        );
        if (dedupData.duplicate) {
          const msg = t`This card is already saved on your account.`;
          setError(msg);
          toast.update(msg, "error");
          setSaving(false);
          onSuccess();
          return;
        }
      } catch {
        /* dedup failed silently — card was attached via SetupIntent */
      }
    }

    toast.update(t`Saved`, "success");
    setSaving(false);
    onSuccess();
  };

  return (
    <div className="bg-card border-border rounded-xl border p-5">
      <h3 className="text-foreground mb-4 text-sm font-semibold">
        <Trans>Add new card</Trans>
      </h3>
      <div className="border-input rounded-lg border p-3">
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: "16px",
                color: "var(--foreground, #1f2937)",
                "::placeholder": { color: "var(--muted-foreground, #9ca3af)" },
              },
              invalid: { color: "var(--destructive, #dc2626)" },
            },
          }}
        />
      </div>
      {error && (
        <div className="text-destructive mt-2 flex items-center gap-1.5 text-sm">
          <AlertCircle className="size-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={saving || !clientSecret || !stripe}
          className="gap-1"
        >
          {saving ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
          <Trans>Save card</Trans>
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </div>
  );
}
